// The interpolation history (§20.8, §23.5): the last 40 decoded snapshots of the current round,
// strictly ascending by room tick. DOM-free.
import type { Snapshot } from '@shared/protocol/snapshot';

export const SNAPSHOT_BUFFER_SIZE = 40;

/**
 * The snapshots around a render tick. `a` is the newest snapshot at or before the tick (the oldest
 * one if the tick precedes them all) and `b` the one after it, with `t` ∈ [0, 1) between them.
 * When `a` is the newest snapshot, `b` is null and `ahead` is how far the tick lies beyond it.
 */
export interface Bracket { a: Snapshot | null; b: Snapshot | null; t: number; ahead: number }

export function createBracket(): Bracket {
  return { a: null, b: null, t: 0, ahead: 0 };
}

export class SnapshotBuffer {
  private readonly snaps: Snapshot[] = [];

  get size(): number {
    return this.snaps.length;
  }

  get latest(): Snapshot | null {
    return this.snaps.length > 0 ? this.snaps[this.snaps.length - 1] : null;
  }

  get oldest(): Snapshot | null {
    return this.snaps.length > 0 ? this.snaps[0] : null;
  }

  /** Appends a snapshot newer than the latest; returns false (and ignores it) otherwise. */
  push(snap: Snapshot): boolean {
    const latest = this.latest;
    if (latest && snap.roomTick <= latest.roomTick) return false;
    this.snaps.push(snap);
    if (this.snaps.length > SNAPSHOT_BUFFER_SIZE) this.snaps.shift();
    return true;
  }

  /** Fills `out` with the snapshots around `renderTick` (room ticks). */
  bracket(renderTick: number, out: Bracket = createBracket()): Bracket {
    const n = this.snaps.length;
    out.a = out.b = null;
    out.t = out.ahead = 0;
    if (n === 0) return out;
    let i = n - 1;
    while (i > 0 && this.snaps[i].roomTick > renderTick) i--;
    const a = this.snaps[i];
    out.a = a;
    if (i === n - 1) {
      out.ahead = Math.max(0, renderTick - a.roomTick);
    } else if (renderTick >= a.roomTick) {
      const b = this.snaps[i + 1];
      out.b = b;
      out.t = (renderTick - a.roomTick) / (b.roomTick - a.roomTick);
    }
    return out;
  }

  /** Drops every snapshot older than `roomTick` (hard resync keeps only the latest). */
  trimBefore(roomTick: number): void {
    let k = 0;
    while (k < this.snaps.length && this.snaps[k].roomTick < roomTick) k++;
    this.snaps.splice(0, k);
  }

  clear(): void {
    this.snaps.length = 0;
  }
}
